import { useState } from 'react';

import { useSearchParams } from 'react-router';

import { Grid } from '@mui/material';

import { FuncionarioAutoComplete, StoreAutoComplete } from 'components/autocompletes';
import { Funcionario } from 'components/autocompletes/FuncionarioAutoComplete';
import { Filter } from 'components/filter';
import { useApplicationContext } from 'context/ApplicationContext';
import { useClientContext } from 'context/ClientContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { FilterValuesProps, useFilter } from 'hooks/useFilter';
import { useValidationUsuario } from 'hooks/useValidationUsuario';
import { useCarrinhoContext } from 'pages/compra/carrinho/contexts';

export function CarrinhoGridFilter() {
    const { hasPermission } = usePermissionsContext();
    const { handleFilterSubmit, handleFilterReset, setDefaultFilters } = useFilter();
    const { formattedStore, user } = useApplicationContext();
    const { setSelectedPedidos } = useCarrinhoContext();
    const { tipoPedido } = useClientContext();
    const { validateUsuario } = useValidationUsuario();
    const [searchParams] = useSearchParams();
    const funcionarioParams = searchParams.get('idUsuarioPedido');
    const hasPermissionFinalizacaoGeral = hasPermission(['COMPRA_PEDIDO_FINALIZACAO_GERAL']);
    const isTipoPedidoFuncionario = tipoPedido === 'funcionario';
    const isTipoPedidoPadrao = tipoPedido === 'padrao';

    const [filterLoja, setFilterLoja] = useState<FilterValuesProps>({
        property: 'idLoja',
        operator: 'in',
        value: [],
        idProperty: 'idLoja',
    });

    const [filterFuncionario, setFilterFuncionario] = useState<FilterValuesProps>({
        property: 'idUsuarioPedido',
        operator: 'eq',
        value: null,
        idProperty: 'idUsuario',
    });

    const filters = [
        { value: filterLoja, setter: setFilterLoja },
        { value: filterFuncionario, setter: setFilterFuncionario },
    ];

    /**
     * Define o filtro de loja de acordo com a loja do usuário logado.
     */
    const setDefaultFilterLoja = () => setFilterLoja((oldState: FilterValuesProps) => ({ ...oldState, value: [formattedStore] }));

    /**
     * Reseta os filtros e define o filtro de loja de acordo com a loja do usuário logado.
     */
    const resetFilter = () => {
        handleFilterReset({ filters });

        setDefaultFilterLoja();
    };

    /**
     * Valida se há um funcionário ou se o usuário logado está válido para executar pedidos.
     */
    const validateFuncionario = () => Boolean(funcionarioParams) || validateUsuario();

    /**
     * Inicia o filtro caso caso não seja um pedido de funcionário ou
     * caso seja um pedido de funcionário e o usuário logado esteja válido.
     */
    const startFilter = () => {
        if (isTipoPedidoFuncionario && !validateFuncionario()) return;

        handleFilterSubmit({ filters });
    };

    /**
     * Define o filtro de funcionário de acordo com o usuário logado caso:
     * - O tipo de pedido seja de funcionário;
     * - O usuário tenha permissão pra finzalizar pedidos;
     * - Não exista um funcionário nos parâmetros da URL (está sendo filtrado).
     */
    const setDefaultFilterFuncionario = () => {
        if (isTipoPedidoFuncionario && hasPermissionFinalizacaoGeral && !funcionarioParams) {
            setFilterFuncionario((oldState: FilterValuesProps) => ({
                ...oldState,
                value: { idUsuario: user.idUsuario, dsUsuario: user.dsUsuario },
            }));
        }
    };

    /**
     * Caso o tipo de pedido seja de funcionário e o usuário logado esteja válido,
     * define o filtro de loja, funcionario e os filtros padrões.
     * Caso o tipo de pedido seja padrão, apenas define os filtros padrões.
     */
    const initFilter = () => {
        if (isTipoPedidoFuncionario) {
            if (!validateFuncionario()) {
                return;
            }

            setDefaultFilterLoja();
            setDefaultFilterFuncionario();

            setDefaultFilters({ filters, preventQueryParamsEmptyStart: true });
        } else if (isTipoPedidoPadrao) {
            setDefaultFilters({ filters, preventQueryParamsEmptyStart: true });
        }
    };

    return (
        <Filter init={initFilter} reset={resetFilter} submit={startFilter}>
            {isTipoPedidoPadrao && (
                <Grid sx={{ flex: 1 }}>
                    <StoreAutoComplete
                        size="small"
                        multiple
                        value={filterLoja.value}
                        onChange={(e, value) => {
                            setFilterLoja((oldState: FilterValuesProps) => ({ ...oldState, value }));

                            setSelectedPedidos([]);
                        }}
                    />
                </Grid>
            )}

            {isTipoPedidoFuncionario && hasPermissionFinalizacaoGeral && (
                <Grid sx={{ flex: 1 }}>
                    <FuncionarioAutoComplete
                        size="small"
                        value={filterFuncionario.value}
                        onChange={(e, value: Funcionario) => {
                            let valueLoja = formattedStore;

                            if (value) {
                                valueLoja = {
                                    idLoja: value.loja.idLoja,
                                    cdLoja: value.loja.cdLoja,
                                    dsNomeFantasia: value.loja.dsNomeFantasia,
                                };
                            }

                            setFilterLoja((oldState: FilterValuesProps) => ({ ...oldState, value: [valueLoja] }));

                            setFilterFuncionario((oldState: FilterValuesProps) => ({
                                ...oldState,
                                value: value && { idUsuario: value.idUsuario, dsUsuario: value.dsUsuario },
                            }));
                        }}
                    />
                </Grid>
            )}
        </Filter>
    );
}
